import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Modal,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { billingAPI, getBillingCheckoutConflict } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import { trackAuthEvent, trackFeatureUsage } from '../../services/analytics';
import { getPlanDisplayName } from '../../utils/planContext';
import { openCheckoutSession } from '../../utils/checkout';
import { syncBillingAfterMutation } from '../../utils/billingSync';
import { colors, spacing } from '../../design-system';
import { Text } from '@/components/ui/text';
import EmbeddedCheckoutModal from './EmbeddedCheckoutModal';
import { confirmDialog, showDialog } from '../../utils/dialogs';
import {
  createCheckoutError,
  getCheckoutErrorDetails,
  markCheckoutErrorShown,
  reportCheckoutFailure,
  showCheckoutFailureDialog,
  type CheckoutFailureFeedback,
} from '../../utils/checkoutFailures';

type RequiredTier = 'tier2' | 'tier3';
type BillingCycle = '1mo' | '3mo' | '6mo';
const CHECKOUT_SURFACE = 'upgrade_modal';

interface UpgradeModalProps {
  visible: boolean;
  onClose: () => void;
  /** The lowest tier that unlocks the gated feature. */
  requiredTier: RequiredTier;
  /** Identifies which feature the user tapped, for analytics + copy. */
  featureKey: string;
}

interface PricingInterval {
  cycle: BillingCycle;
  months: number;
  eur: { cents: number };
  usd: { cents: number };
  try: { cents: number };
}

interface PricingTier {
  tier: 'tier1' | 'tier2' | 'tier3';
  recommended?: boolean;
  intervals?: PricingInterval[];
}

const FEATURE_BENEFIT_KEYS: Record<RequiredTier, string[]> = {
  tier2: ['upgrade.benefit.tier2_1', 'upgrade.benefit.tier2_2', 'upgrade.benefit.tier2_3'],
  tier3: ['upgrade.benefit.tier3_1', 'upgrade.benefit.tier3_2', 'upgrade.benefit.tier3_3'],
};

/**
 * In-app upgrade paywall.
 *
 * Replaces the old non-interactive "Upgrade" lock with a proper modal that
 * shows which package unlocks the tapped feature, live pricing with
 * multi-month discount options, and a CTA that opens the in-app Stripe
 * embedded checkout (web) / hosted checkout (native).
 */
export default function UpgradeModal({ visible, onClose, requiredTier, featureKey }: UpgradeModalProps) {
  const { t } = useTranslation();
  const { refreshAuth } = useAuth();
  const isWeb = Platform.OS === 'web';

  const [loadingPricing, setLoadingPricing] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [intervals, setIntervals] = useState<PricingInterval[]>([]);
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>(requiredTier === 'tier3' ? '3mo' : '1mo');
  const [checkoutVisible, setCheckoutVisible] = useState(false);
  const [launching, setLaunching] = useState(false);

  // Fire the paywall-viewed event and load pricing whenever the modal opens.
  useEffect(() => {
    if (!visible) return;
    trackFeatureUsage('paywall', 'viewed', { required_tier: requiredTier, feature: featureKey });

    let cancelled = false;
    (async () => {
      try {
        setLoadingPricing(true);
        const p = await billingAPI.pricing();
        if (cancelled) return;
        const tier = (p.tiers as PricingTier[] | undefined)?.find((x) => x.tier === requiredTier);
        const ivs = (tier?.intervals as PricingInterval[] | undefined) || [];
        setCurrency(p.currency || 'USD');
        setIntervals(ivs);
        if (ivs.length > 0 && !ivs.some((iv) => iv.cycle === selectedCycle)) {
          setSelectedCycle(ivs[0].cycle);
        }
      } catch {
        // Pricing is best-effort; the CTA still works (checkout resolves price server-side).
      } finally {
        if (!cancelled) setLoadingPricing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, requiredTier, featureKey]);

  const centsFor = (iv: PricingInterval): number => {
    const key = currency.toLowerCase() as 'eur' | 'usd' | 'try';
    return iv?.[key]?.cents ?? iv?.usd?.cents ?? 0;
  };

  const formatMoney = (cents: number): string => {
    const code = (currency || 'USD').toUpperCase();
    try {
      const locale = code === 'TRY' ? 'tr-TR' : 'en-US';
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(cents / 100);
    } catch {
      return `${code} ${(cents / 100).toFixed(2)}`;
    }
  };

  // Highest per-month price across cycles = baseline for "save X%".
  const baselinePerMonth = useMemo(() => {
    if (!intervals.length) return 0;
    return Math.max(...intervals.map((iv) => centsFor(iv) / Math.max(iv.months, 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervals, currency]);

  const savePct = (iv: PricingInterval): number => {
    if (!baselinePerMonth) return 0;
    const perMonth = centsFor(iv) / Math.max(iv.months, 1);
    return Math.round((1 - perMonth / baselinePerMonth) * 100);
  };

  const selectedInterval = intervals.find((iv) => iv.cycle === selectedCycle) || intervals[0];

  const showBillingAlert = (titleKey: string, messageKey: string) => {
    showDialog(t(titleKey), t(messageKey));
  };

  const confirmBillingAction = (
    titleKey: string,
    messageKey: string,
    confirmKey: string,
  ): Promise<boolean> => {
    const title = t(titleKey);
    const message = t(messageKey);
    const confirmLabel = t(confirmKey);
    return confirmDialog(title, message, confirmLabel, t('billing.conflicts.dismissAction'));
  };

  const resumeCurrentSubscription = async () => {
    await billingAPI.resumeSubscription();
    await syncBillingAfterMutation(refreshAuth);
    showBillingAlert('billing.conflicts.resumeSuccessTitle', 'billing.conflicts.resumeSuccessMessage');
    onClose();
  };

  const changeCurrentSubscription = async () => {
    await billingAPI.changePlan(requiredTier, selectedCycle);
    await syncBillingAfterMutation(refreshAuth);
    showBillingAlert('billing.conflicts.changePlanSuccessTitle', 'billing.conflicts.changePlanSuccessMessage');
    onClose();
  };

  const showCheckoutError = async (error: unknown, retry: () => unknown | Promise<unknown>) => {
    await showCheckoutFailureDialog({
      error,
      surface: CHECKOUT_SURFACE,
      checkoutKey: `${requiredTier}:${selectedCycle}`,
      properties: {
        plan: requiredTier,
        billing_period: selectedCycle,
      },
      retry,
      t,
    });
  };

  const handleCheckoutConflict = async (
    error: any,
    initialFeedback?: CheckoutFailureFeedback,
  ): Promise<boolean> => {
    const conflict = error?.billingCheckoutConflict || getBillingCheckoutConflict(error);
    if (!conflict) return false;

    const retryOriginalCheckoutOnce = async (conflictCode: string): Promise<boolean> => {
      try {
        const response = await billingAPI.checkout('b2c', requiredTier, selectedCycle);
        if (!response.checkout_url) {
          throw createCheckoutError('missing_checkout_url');
        }
        await openCheckoutSession(response.checkout_url);
        await refreshAuth();
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflictCode,
          resolution: 'checkout_retry',
          surface: CHECKOUT_SURFACE,
        });
        onClose();
      } catch (retryError: any) {
        const retryConflict = getBillingCheckoutConflict(retryError);
        const diagnostics = getCheckoutErrorDetails(retryError);
        trackAuthEvent('checkout_conflict_resolution_failed', {
          conflict_code: retryConflict?.code || conflictCode,
          resolution: 'checkout_retry',
          error_code: diagnostics.errorCode,
          error_type: diagnostics.errorType,
          surface: CHECKOUT_SURFACE,
        });
        await showCheckoutError(retryError, () => retryOriginalCheckoutOnce(conflictCode));
      }
      return true;
    };

    if (conflict.action === 'checkout') {
      return retryOriginalCheckoutOnce(conflict.code);
    }

    if (conflict.code === 'resume_available') {
      if (initialFeedback) {
        markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
          plan: requiredTier,
          billing_period: selectedCycle,
        });
      }
      trackAuthEvent('checkout_conflict_shown', {
        conflict_code: conflict.code,
        action: conflict.action || 'resume',
        plan: requiredTier,
        billing_period: selectedCycle,
        surface: CHECKOUT_SURFACE,
      });
      const confirmed = await confirmBillingAction(
        'billing.conflicts.resumeAvailableTitle',
        'billing.conflicts.resumeAvailableMessage',
        'billing.conflicts.resumeAction',
      );
      if (confirmed) {
        try {
          await resumeCurrentSubscription();
          trackAuthEvent('checkout_conflict_resolved', {
            conflict_code: conflict.code,
            resolution: 'resume',
            surface: CHECKOUT_SURFACE,
          });
        } catch (actionError: any) {
          const actionConflict = getBillingCheckoutConflict(actionError);
          if (actionConflict?.action === 'checkout') {
            return retryOriginalCheckoutOnce(actionConflict.code);
          }
          const diagnostics = getCheckoutErrorDetails(actionError);
          trackAuthEvent('checkout_conflict_resolution_failed', {
            conflict_code: conflict.code,
            resolution: 'resume',
            error_code: diagnostics.errorCode,
            error_type: diagnostics.errorType,
            surface: CHECKOUT_SURFACE,
          });
          await showCheckoutError(actionError, resumeCurrentSubscription);
        }
      } else {
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflict.code,
          resolution: 'dismissed',
          surface: CHECKOUT_SURFACE,
        });
      }
      return true;
    }

    const titleKey = conflict.code === 'already_subscribed'
      ? 'billing.conflicts.alreadySubscribedTitle'
      : 'billing.conflicts.pendingCancellationTitle';
    const messageKey = conflict.code === 'already_subscribed'
      ? 'billing.conflicts.alreadySubscribedMessage'
      : 'billing.conflicts.pendingCancellationMessage';
    if (initialFeedback) {
      markCheckoutErrorShown(initialFeedback, CHECKOUT_SURFACE, {
        plan: requiredTier,
        billing_period: selectedCycle,
      });
    }
    trackAuthEvent('checkout_conflict_shown', {
      conflict_code: conflict.code,
      action: conflict.action || 'change_plan',
      plan: requiredTier,
      billing_period: selectedCycle,
      surface: CHECKOUT_SURFACE,
    });
    const confirmed = await confirmBillingAction(
      titleKey,
      messageKey,
      'billing.conflicts.changePlanAction',
    );
    if (confirmed) {
      try {
        await changeCurrentSubscription();
        trackAuthEvent('checkout_conflict_resolved', {
          conflict_code: conflict.code,
          resolution: 'change_plan',
          surface: CHECKOUT_SURFACE,
        });
      } catch (actionError: any) {
        const actionConflict = getBillingCheckoutConflict(actionError);
        if (actionConflict?.action === 'checkout') {
          return retryOriginalCheckoutOnce(actionConflict.code);
        }
        const diagnostics = getCheckoutErrorDetails(actionError);
        trackAuthEvent('checkout_conflict_resolution_failed', {
          conflict_code: conflict.code,
          resolution: 'change_plan',
          error_code: diagnostics.errorCode,
          error_type: diagnostics.errorType,
          surface: CHECKOUT_SURFACE,
        });
        await showCheckoutError(actionError, changeCurrentSubscription);
      }
    } else {
      trackAuthEvent('checkout_conflict_resolved', {
        conflict_code: conflict.code,
        resolution: 'dismissed',
        surface: CHECKOUT_SURFACE,
      });
    }
    return true;
  };

  const handleUpgrade = async () => {
    trackFeatureUsage('paywall', 'cta_clicked', {
      required_tier: requiredTier,
      feature: featureKey,
      cycle: selectedCycle,
    });
    if (isWeb) {
      setCheckoutVisible(true);
      return;
    }
    // Native: hosted Stripe checkout.
    try {
      setLaunching(true);
      const resp = await billingAPI.checkout('b2c', requiredTier, selectedCycle);
      if (!resp?.checkout_url) {
        throw createCheckoutError('missing_checkout_url');
      }
      await openCheckoutSession(resp.checkout_url);
      await refreshAuth();
      onClose();
    } catch (error: any) {
      const properties = {
        plan: requiredTier,
        billing_period: selectedCycle,
      };
      const feedback = reportCheckoutFailure({
        error,
        surface: CHECKOUT_SURFACE,
        checkoutKey: `${requiredTier}:${selectedCycle}`,
        properties,
        t,
      });
      const handled = await handleCheckoutConflict(error, feedback);
      if (!handled) {
        await showCheckoutFailureDialog({
          error,
          feedback,
          surface: CHECKOUT_SURFACE,
          checkoutKey: `${requiredTier}:${selectedCycle}`,
          properties,
          retry: handleUpgrade,
          t,
        });
      }
    } finally {
      setLaunching(false);
    }
  };

  const handleCheckoutComplete = async () => {
    await syncBillingAfterMutation(refreshAuth);
    setCheckoutVisible(false);
    onClose();
  };

  const packageName = getPlanDisplayName(requiredTier);

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.card} testID="upgrade-modal">
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.badge}>
              <MaterialIcons name="workspace-premium" size={20} color="#FFFFFF" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.title}>{t('upgrade.title')}</Text>
              <Text style={styles.subtitle}>{t('upgrade.subtitle')}</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton} accessibilityLabel={t('common.close')}>
              <MaterialIcons name="close" size={22} color={colors.text.secondary} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.body} contentContainerStyle={{ gap: spacing.md }}>
            {/* What unlocks */}
            <View style={styles.unlockRow}>
              <MaterialIcons name="lock-open" size={18} color={colors.primary} />
              <Text style={styles.unlockText}>
                {t('upgrade.unlocks')}{' '}
                <Text style={styles.unlockFeature}>{t(`upgrade.feature.${featureKey}`)}</Text>
              </Text>
            </View>

            {/* Package */}
            <View style={styles.packageRow}>
              <Text style={styles.packageLabel}>{t('upgrade.packageLabel')}</Text>
              <Text style={styles.packageName}>{packageName}</Text>
            </View>

            {/* Benefits */}
            <View style={{ gap: 6 }}>
              {FEATURE_BENEFIT_KEYS[requiredTier].map((k) => (
                <View key={k} style={styles.benefitRow}>
                  <MaterialIcons name="check-circle" size={16} color={colors.primary} />
                  <Text style={styles.benefitText}>{t(k)}</Text>
                </View>
              ))}
            </View>

            {/* Pricing / cycle selector */}
            {loadingPricing ? (
              <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.md }} />
            ) : intervals.length > 0 ? (
              <View style={styles.cycleGroup}>
                {intervals.length > 1 && (
                  <Text style={styles.cyclePickerLabel}>{t('upgrade.cyclePicker')}</Text>
                )}
                <View style={styles.cycleRow}>
                  {intervals.map((iv) => {
                    const active = iv.cycle === selectedCycle;
                    const pct = savePct(iv);
                    return (
                      <TouchableOpacity
                        key={iv.cycle}
                        onPress={() => setSelectedCycle(iv.cycle)}
                        style={[styles.cycleChip, active && styles.cycleChipActive]}
                      >
                        <Text style={[styles.cycleChipText, active && styles.cycleChipTextActive]}>
                          {t(`upgrade.cycle.${iv.cycle}`)}
                        </Text>
                        {pct > 0 && (
                          <Text style={[styles.cycleSave, active && styles.cycleSaveActive]}>
                            {t('upgrade.save', { pct: String(pct) })}
                          </Text>
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>
                {selectedInterval && (
                  <View style={styles.priceRow}>
                    <Text style={styles.price}>{formatMoney(centsFor(selectedInterval))}</Text>
                    <Text style={styles.priceMeta}>
                      {' '}
                      / {t(`upgrade.cycle.${selectedInterval.cycle}`)}
                      {selectedInterval.months > 1
                        ? `  ·  ${formatMoney(centsFor(selectedInterval) / selectedInterval.months)} ${t('upgrade.perMonth')}`
                        : ''}
                    </Text>
                  </View>
                )}
              </View>
            ) : null}

            {/* CTA */}
            <TouchableOpacity
              style={styles.cta}
              onPress={handleUpgrade}
              disabled={launching}
              accessibilityRole="button"
              testID="upgrade-modal-cta"
            >
              {launching ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.ctaText}>{t('upgrade.cta')}</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity style={styles.secondary} onPress={onClose}>
              <Text style={styles.secondaryText}>{t('upgrade.maybeLater')}</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
      </View>

      {/* Nested embedded checkout (web only; renders null on native). */}
      <EmbeddedCheckoutModal
        visible={checkoutVisible}
        planTier={requiredTier}
        billingCycle={selectedCycle}
        onConflictResolved={async () => {
          await refreshAuth();
          setCheckoutVisible(false);
          onClose();
        }}
        onComplete={handleCheckoutComplete}
        onClose={() => setCheckoutVisible(false)}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(17, 24, 39, 0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    maxHeight: '88%',
    backgroundColor: colors.background,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  badge: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.text.primary,
  },
  subtitle: {
    fontSize: 13,
    color: colors.text.secondary,
    marginTop: 2,
  },
  closeButton: {
    padding: spacing.xs,
  },
  body: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  unlockRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  unlockText: {
    flex: 1,
    fontSize: 14,
    color: colors.text.secondary,
  },
  unlockFeature: {
    fontWeight: '700',
    color: colors.text.primary,
  },
  packageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  packageLabel: {
    fontSize: 13,
    color: colors.text.secondary,
  },
  packageName: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary,
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  benefitText: {
    flex: 1,
    fontSize: 14,
    color: colors.text.primary,
  },
  cycleGroup: {
    gap: 8,
    marginTop: 4,
  },
  cyclePickerLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text.secondary,
  },
  cycleRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  cycleChip: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: colors.border,
    alignItems: 'center',
    minWidth: 92,
  },
  cycleChipActive: {
    borderColor: colors.primary,
    backgroundColor: '#EEF2FB',
  },
  cycleChipText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.primary,
  },
  cycleChipTextActive: {
    color: colors.primary,
  },
  cycleSave: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.text.secondary,
    marginTop: 2,
  },
  cycleSaveActive: {
    color: colors.primary,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
  },
  price: {
    fontSize: 22,
    fontWeight: '800',
    color: colors.text.primary,
  },
  priceMeta: {
    fontSize: 13,
    color: colors.text.secondary,
  },
  cta: {
    marginTop: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  secondary: {
    paddingVertical: spacing.sm,
    alignItems: 'center',
  },
  secondaryText: {
    color: colors.text.secondary,
    fontSize: 14,
    fontWeight: '600',
  },
});
